import { type MarketCalendar, marketCalendar } from '@alphadesk/ontology/calendar.ts';
import {
  DAY_PART_LABEL,
  type DayPart,
  type DayPartSpan,
  dayMarks,
  dayPartSaid,
  dayPartSpan,
  type Mark,
} from '@alphadesk/ontology/day-part.ts';
import {
  addDays,
  istClock,
  MINUTE_MS,
  monthSaid,
  todayInIst,
  weekdayLongSaid,
} from '@alphadesk/ontology/when.ts';
import {
  API_PATHS,
  type BriefLine,
  type Home,
  homeSchema,
  type Need,
} from '@alphadesk/web/contract.ts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCalendar } from './calendar.ts';
import { useNow } from './clock.ts';
import { fmtClockOf } from './format.ts';
import { type Read, useRead } from './reads.ts';
import type { StreamState } from './stream.ts';

const HOME_POLL_MS = 60 * 1000;

const ASK_AGAIN_MS = 1000;

type HomeFrames = Pick<StreamState, 'positions' | 'recommendations' | 'notifications'>;

export interface HomeState {
  read: Read<Home>;
  /** The server's own calendar, rebuilt here; null until it is read. */
  calendar: MarketCalendar | null;
  /** On the server's clock, turning on the minute. */
  now: Date;
  today: string;
  /** Off the calendar once it is read; before that, only the answer's part is known. */
  span: DayPartSpan | null;
  part: DayPart | null;
  marks: Mark[];
}

/** A turn is from one part to another; the first part to be known is not one. */
function useTurns(part: DayPart | null): number {
  const [held, setHeld] = useState({ part, turns: 0 });
  if (held.part === part) return held.turns;
  const turns = held.part !== null && part !== null ? held.turns + 1 : held.turns;
  setHeld({ part, turns });
  return turns;
}

/** Read once for the page and its rail; `on` only while Home is the page. */
export function useHome(on: boolean, frames: HomeFrames): HomeState {
  const read = useRead(on ? API_PATHS.home : null, homeSchema, { every: HOME_POLL_MS });
  const { state: calendarRead } = useCalendar(on);
  const now = useNow(MINUTE_MS, true);
  const today = todayInIst(now);

  const known = calendarRead.status === 'ok' ? calendarRead.data : null;
  const calendar = useMemo(() => (known ? marketCalendar(known.days) : null), [known]);
  const span = useMemo(() => (calendar ? dayPartSpan(calendar, now) : null), [calendar, now]);
  const marks = useMemo(
    () => (calendar && known ? dayMarks(calendar, known.expiries, today) : []),
    [calendar, known, today],
  );
  const part = span?.part ?? (read.state.status === 'ok' ? read.state.data.part : null);
  const turns = useTurns(span?.part ?? null);

  // A frame or a turn is what to ask again on, not what the answer is read from; the page's
  // first render has asked already.
  const { refresh } = read;
  const { positions, recommendations, notifications } = frames;
  const asked = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: as above
  useEffect(() => {
    if (!on) {
      asked.current = false;
      return;
    }
    if (!asked.current) {
      asked.current = true;
      return;
    }
    const timer = setTimeout(refresh, ASK_AGAIN_MS);
    return () => clearTimeout(timer);
  }, [on, refresh, positions, recommendations, notifications, turns]);

  return { read, calendar, now, today, span, part, marks };
}

/**
 * The server's answer, while it is for the part the reader is in: just after a turn, or back on
 * Home hours later, the one on hand is another part's until the next lands.
 */
export function answerOf(home: HomeState): Home | null {
  const { state } = home.read;
  return state.status === 'ok' && state.data.part === home.part ? state.data : null;
}

/** "Tuesday 29 September". */
export const homeDateSaid = (day: string): string =>
  `${weekdayLongSaid(day)} ${Number(day.slice(8, 10))} ${monthSaid(day)}`;

export function partSaid(home: HomeState): string | null {
  if (home.part === null) return null;
  return home.calendar ? dayPartSaid(home.calendar, home.now) : DAY_PART_LABEL[home.part];
}

/** A day off can run to a later day's midnight, which reads as the day it gives way to. */
export function untilSaid(to: Date, now: Date): string {
  const today = todayInIst(now);
  const day = todayInIst(to);
  const clock = istClock(to);
  if (day === today) return `until ${clock}`;
  if (clock !== '00:00') return `until ${weekdayLongSaid(day)} ${clock}`;
  return day === addDays(today, 1) ? 'until midnight' : `until ${weekdayLongSaid(day)}`;
}

/** "before 09:15", or "before Wed 09:15" when it falls on another day. */
export const bySaid = (by: string, now: Date): string => `before ${fmtClockOf(by, now)}`;

/** At most this many in Needs you; the rest are a link to the inbox. */
export const NEEDS_SHOWN = 5;

/**
 * While the reader is on the list, its rows keep the order they had when they arrived there: a row
 * that lands meanwhile waits, counted, and one that went is simply gone.
 */
export function heldOrder<T extends { key: string }>(
  rows: readonly T[],
  held: readonly string[] | null,
): { shown: T[]; waiting: number } {
  if (held === null) return { shown: [...rows], waiting: 0 };
  const byKey = new Map(rows.map((row) => [row.key, row]));
  const kept = new Set(held);
  return {
    shown: held.flatMap((key) => byKey.get(key) ?? []),
    waiting: rows.filter((row) => !kept.has(row.key)).length,
  };
}

/**
 * Where Needs you stands. `listed` is drawn as the card: rows, or the server's word that it could
 * not read them. `clear` is the masthead's chip, only off a read whose every line was read. Before
 * the first read lands it is neither, so nothing says all clear.
 */
export type NeedsState =
  | { at: 'waiting' }
  | { at: 'clear'; standing: BriefLine | null }
  | { at: 'listed'; needs: Need[]; unread: boolean };

/** `followShown`: the path card already asks them to follow an analyst, so Needs you does not. */
export function needsOf(home: HomeState, followShown = false): NeedsState {
  const { state } = home.read;
  if (state.status !== 'ok') return { at: 'waiting' };
  const { unread, standing } = state.data.needs;
  const rows = followShown
    ? state.data.needs.rows.filter(
        (need) => !(need.from === 'line' && need.line.kind === 'follow-an-analyst'),
      )
    : state.data.needs.rows;
  if (rows.length > 0 || unread) return { at: 'listed', needs: rows, unread };
  return { at: 'clear', standing };
}
